import { Comment as CommentIcon } from "@mui/icons-material";
import {
  Badge,
  Box,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { type MouseEvent as ReactMouseEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DocumentStatusIcon } from "src/components/DocumentStatusIcon";
import type { DocStatus } from "src/types";
import { docStatusDictionary } from "src/utils";
import { buildDocumentRoute } from "src/utils/navigation";
import { DashboardEmptyStateText } from "./DashboardEmptyStateText";
import { DASHBOARD_ITEM_PADDING_CLASS_NAME } from "./dashboardItemPadding";

export type DashboardCommentThreadDocument = {
  documentId: string;
  documentTitle: string;
  deviceId: string | null;
  status: DocStatus;
  latestVersionId: string;
};

type DashboardCommentThreadsDocumentListProps<
  TDocument extends DashboardCommentThreadDocument,
> = {
  documents: TDocument[];
  selectedDeviceId?: string;
  fallbackDeviceId?: string;
  emptyText: string;
  threadStatusLabel: "open" | "resolved";
  badgeColor: "primary" | "success";
  getThreadIds: (document: TDocument) => string[];
  rowTestIdPrefix: string;
  labelGroupTestIdPrefix: string;
  statusIconTestIdPrefix: string;
  commentsButtonTestIdPrefix: string;
  menuIdSuffix: string;
};

type DashboardCommentThreadDocumentRowProps<
  TDocument extends DashboardCommentThreadDocument,
> = Omit<
  DashboardCommentThreadsDocumentListProps<TDocument>,
  "documents" | "emptyText"
> & {
  document: TDocument;
};

const getThreadCountLabel = (
  count: number,
  threadStatusLabel: "open" | "resolved"
) => `${count} ${threadStatusLabel} ${count === 1 ? "thread" : "threads"}`;

const DashboardCommentThreadDocumentRow = <
  TDocument extends DashboardCommentThreadDocument,
>({
  document,
  selectedDeviceId,
  fallbackDeviceId,
  threadStatusLabel,
  badgeColor,
  getThreadIds,
  rowTestIdPrefix,
  labelGroupTestIdPrefix,
  statusIconTestIdPrefix,
  commentsButtonTestIdPrefix,
  menuIdSuffix,
}: DashboardCommentThreadDocumentRowProps<TDocument>) => {
  const { orgId = "" } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const documentStatus = document.status;
  const statusTooltipTitle = docStatusDictionary[documentStatus].label;
  const effectiveDeviceId =
    document.deviceId ?? selectedDeviceId ?? fallbackDeviceId;
  const threadIds = getThreadIds(document);
  const firstThreadId = threadIds[0];
  const threadCountLabel = getThreadCountLabel(
    threadIds.length,
    threadStatusLabel
  );
  const documentTitleId = `${document.documentId}-${menuIdSuffix}-document-title`;
  const threadCountId = `${document.documentId}-${menuIdSuffix}-thread-count`;
  const documentRoute = effectiveDeviceId
    ? buildDocumentRoute({
        orgId,
        deviceId: effectiveDeviceId,
        documentId: document.documentId,
      })
    : undefined;
  const firstThreadRoute =
    documentRoute && firstThreadId
      ? `${documentRoute}?commentThreadId=${firstThreadId}`
      : undefined;

  const handleDocumentItemClick = (event: ReactMouseEvent) => {
    if (documentRoute) {
      event.stopPropagation();
      navigate(documentRoute);
    }
  };

  const handleToggleCommentMenu = (event: ReactMouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!firstThreadRoute) {
      return;
    }

    navigate(firstThreadRoute);
  };

  return (
    <Box
      className={[
        "my-1 flex min-w-0 items-center justify-between gap-2 rounded hover:bg-gray-50",
        DASHBOARD_ITEM_PADDING_CLASS_NAME,
      ].join(" ")}
      data-testid={`${rowTestIdPrefix}-${document.documentId}`}
    >
      <Box
        component={documentRoute ? "button" : "div"}
        type={documentRoute ? "button" : undefined}
        aria-describedby={documentRoute ? threadCountId : undefined}
        aria-labelledby={documentRoute ? documentTitleId : undefined}
        className={[
          "flex min-w-0 flex-1 items-center justify-start gap-2 text-left",
          documentRoute
            ? "-my-2 -ml-3 cursor-pointer rounded border-0 bg-transparent py-2 pl-3 pr-2 font-inherit text-inherit"
            : "",
        ].join(" ")}
        data-testid={`${labelGroupTestIdPrefix}-${document.documentId}`}
        onClick={documentRoute ? handleDocumentItemClick : undefined}
      >
        {statusTooltipTitle ? (
          <Tooltip describeChild placement="top" title={statusTooltipTitle}>
            <span className="inline-flex">
              <DocumentStatusIcon
                reserveSpace
                size={8}
                status={documentStatus}
                testId={`${statusIconTestIdPrefix}-${document.documentId}`}
              />
            </span>
          </Tooltip>
        ) : (
          <DocumentStatusIcon
            reserveSpace
            size={8}
            status={documentStatus}
            testId={`${statusIconTestIdPrefix}-${document.documentId}`}
          />
        )}
        <Typography variant="body2" className="min-w-0 truncate">
          <Box component="span" id={documentTitleId}>
            {document.documentTitle}
          </Box>
          <Box component="span" id={threadCountId} className="sr-only">
            {threadCountLabel}
          </Box>
        </Typography>
      </Box>
      <Box className="ml-auto flex shrink-0 items-center">
        <Tooltip title="Open comments">
          <Badge badgeContent={threadIds.length} color={badgeColor} max={99}>
            <IconButton
              aria-label={`Open comments for ${document.documentTitle}, ${threadCountLabel}`}
              data-testid={`${commentsButtonTestIdPrefix}-${document.documentId}`}
              disabled={!firstThreadRoute}
              onClick={handleToggleCommentMenu}
              size="small"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <CommentIcon fontSize="small" />
            </IconButton>
          </Badge>
        </Tooltip>
      </Box>
    </Box>
  );
};

export const DashboardCommentThreadsDocumentList = <
  TDocument extends DashboardCommentThreadDocument,
>({
  documents,
  selectedDeviceId,
  fallbackDeviceId,
  emptyText,
  ...rowProps
}: DashboardCommentThreadsDocumentListProps<TDocument>) => {
  if (documents.length === 0) {
    return <DashboardEmptyStateText>{emptyText}</DashboardEmptyStateText>;
  }

  return (
    <Box>
      {documents.map((document) => (
        <DashboardCommentThreadDocumentRow
          key={`${document.documentId}-${document.latestVersionId}`}
          document={document}
          selectedDeviceId={selectedDeviceId}
          fallbackDeviceId={fallbackDeviceId}
          {...rowProps}
        />
      ))}
    </Box>
  );
};
